import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useCallback, useMemo, useState } from 'react';
import { useLocationOrders } from './useLocationOrders';

/**
 * Hook centralizado para la gestion de estado y logica de LocationOrders.
 * Maneja filtros, columnas visibles y props compartidas entre vistas.
 */
export const useLocationOrdersMain = (
  searchTerm?: string,
  externalFilterConditions?: FilterConditionOutput[]
) => {
  // Estado de filtros
  const [filterConditions, setFilterConditions] =
    useState<FilterConditionOutput[]>([]);
  const [sorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(
    null
  );

  // Columnas visibles (habilita ColumnSelector)
  const DEFAULT_COLUMNS: string[] = [
    'id',
    'source_id',
    'cart_id',
    'status',
    'created_at',
  ];
  const [selectedColumns, setSelectedColumns] =
    useState<string[]>(DEFAULT_COLUMNS);

  const handleColumnsChange = useCallback((newSelectedColumns: string[]) => {
    setSelectedColumns(newSelectedColumns);
  }, []);

  // Procesar filtros simples para la API
  const processedFilters = useMemo(() => {
    try {
      let statusValue: string | undefined;
      let fromDate: string | undefined;
      let toDate: string | undefined;

      // 🎯 Usar filtros externos si están disponibles, sino usar los internos
      const conditionsToProcess = externalFilterConditions || filterConditions;

      conditionsToProcess.forEach((condition) => {
        // Solo procesar filtros con acción "equals" para status
        if (condition.action === 'equals' && condition.column === 'status') {
          statusValue = String(condition.value);
        } else if (condition.action === 'between' && condition.column === 'created_at') {
          // Procesar rango de fechas (date-range)
          // El formato del valor es "YYYY-MM-DD to YYYY-MM-DD"
          if (condition.value && typeof condition.value === 'string') {
            const [from, to] = condition.value.split(' to ');
            if (from) {
              // Ya viene en formato YYYY-MM-DD
              fromDate = from.trim();
            }
            if (to) {
              // Ya viene en formato YYYY-MM-DD
              toDate = to.trim();
            }
          }
        }
      });

      return {
        ...(statusValue && { status: statusValue }),
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
        // Nota: source_id no está en los parámetros del endpoint según la especificación
        // Se omite ya que el endpoint solo acepta: status, from_date, to_date, search
      };
    } catch (error) {
      return {};
    }
  }, [filterConditions, externalFilterConditions]);

  // Filtros para el hook de datos
  const queryFilters = useMemo(
    () => ({
      search: searchTerm || '',
      ...processedFilters,
    }),
    [searchTerm, processedFilters]
  );

  // Datos con infinite scroll
  const {
    locationorders,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
  } = useLocationOrders(queryFilters);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
    },
    []
  );

  // Props compartidas para desktop/mobile
  const locationordersViewProps = useMemo(
    () => ({
      locationorders,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch,
      sorting,
    }),
    [
      locationorders,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch,
      sorting,
    ]
  );

  const hasActiveFilters = useMemo(() => {
    return (
      filterConditions.length > 0 ||
      sorting !== null
    );
  }, [filterConditions.length, sorting]);

  return {
    // Estados
    filterConditions,
    sorting,
    selectedColumns,

    // Datos
    locationorders,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    locationordersViewProps,

    // Handlers
    handleFilterChange,
    handleColumnsChange,
    refetch,

    // Infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  };
};
